import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Button, Typography, Radio, Divider } from "antd";
import axios from "axios";
import { toast } from "react-toastify";

const { Title } = Typography;

const cardElementStyle = {
    style: {
        base: {
            color: "#fff",
            fontSize: "16px",
            fontFamily: "Outfit, sans-serif",
            iconColor: "#00e0ff",
            "::placeholder": { color: "#a0a0a0" },
        },
        invalid: { color: "#ff4d4d" },
    },
    hidePostalCode: true,
};

const StripePaymentForm = () => {
    const stripe = useStripe();
    const elements = useElements();
    const navigate = useNavigate();
    const location = useLocation();

    const [loading, setLoading] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState("stripe");

    const { clientSecret, orderNumber, totalAmount } = location.state || {};
    const handleStripePayment = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setLoading(true);
        const cardElement = elements.getElement(CardElement);

        const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
            payment_method: { card: cardElement },
        });

        if (error) {
            toast.error(error.message);
            setLoading(false);
            return;
        }

        if (paymentIntent.status === "succeeded") {
            try {
                const res = await axios.post(`${process.env.REACT_APP_API_URL}api/payments/finalize`, {
                    order_number: orderNumber,
                });
                if (res.data.status === true) {
                    Object.keys(localStorage).forEach((key) => {
                        if (!isNaN(key) || key === "order") localStorage.removeItem(key);
                    });
                    window.dispatchEvent(new Event("cartUpdated"));
                    toast.success("Payment successful!");
                    navigate("/thankyou");
                }
            } catch {
                toast.error("Error finalizing payment.");
            } finally {
                setLoading(false);
            }
        } else {
            toast.error("Payment failed. Please try again.");
            setLoading(false);
        }
    };

    const handlePayPalPayment = async () => {
        if (!orderNumber) {
            toast.error("Order number missing!");
            return;
        }

        setLoading(true);
        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_API_URL}api/paypal/create-order`,
                { order_number: orderNumber }
            );

            if (data?.status && data?.approve_link) {
                toast.success("Redirect to PayPal");
                Object.keys(localStorage).forEach((key) => {
                    if (!isNaN(key) || key === "order") localStorage.removeItem(key);
                });
                window.dispatchEvent(new Event("cartUpdated"));
                window.location.href = data.approve_link;
            } else {
                toast.error("Failed to create PayPal order.");
            }
        } catch (error) {
            console.error(error);
            toast.error("PayPal initialization failed.");
        } finally {
            setLoading(false);
        }
    };

    if (!clientSecret && paymentMethod === "stripe") {
        return (
            <div className="bg-black text-white text-center pt-[60px] pb-[60px] mt-[160px]">
                <Title level={3} style={{ color: "#00e0ff" }}>Invalid Payment Session</Title>
                <Button onClick={() => navigate("/checkout")}>Go Back</Button>
            </div>
        );
    }

    return (
        <div className="bg-black text-white flex items-center justify-center pt-[60px] pb-[60px] mt-[160px]">
            <div className="max-w-md w-full border border-gray-700 rounded-2xl p-6">
                <Title level={3} style={{ color: "#00e0ff", textAlign: "center", marginBottom: 20 }}>
                    Choose Payment Method
                </Title>

                <Radio.Group
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    value={paymentMethod}
                    className="flex justify-center gap-2 mb-8"
                >
                    <Radio.Button
                        value="stripe"
                        className={`!rounded-xl !text-lg !font-semibold !transition-all !duration-300 flex  
                                ${paymentMethod === "stripe"
                                ? "!bg-cyan-400 !text-black !shadow-[0_0_15px_rgba(0,224,255,0.6)] !border-cyan-400"
                                : "!bg-[#111] !text-gray-400 !border-[#333] hover:!border-cyan-400 hover:!text-cyan-400"
                            }`}
                    >
                        Credit / Debit Card
                    </Radio.Button>

                    <Radio.Button
                        value="paypal"
                        className={`!rounded-xl !text-lg !font-semibold !transition-all !duration-300 flex
                                ${paymentMethod === "paypal"
                                ? "!bg-[#ffc439] !text-black !shadow-[0_0_15px_rgba(255,196,57,0.6)] !border-[#ffc439]"
                                : "!bg-[#111] !text-gray-400 !border-[#333] hover:!border-[#ffc439] hover:!text-[#ffc439]"
                            }`}
                    >
                        PayPal
                    </Radio.Button>
                </Radio.Group>

                <Divider style={{ borderColor: "#333" }} />

                {paymentMethod === "stripe" ? (
                    <form onSubmit={handleStripePayment}>
                        <div className="bg-[#111] border border-[#333] rounded-lg p-4 mb-4">
                            <CardElement options={cardElementStyle} />
                        </div>
                        <Button
                            htmlType="submit"
                            type="primary"
                            loading={loading}
                            block
                            style={{
                                background: "#00e0ff",
                                color: "#000",
                                border: "none",
                                borderRadius: "50px",
                                fontWeight: 600,
                                fontSize: "16px",
                                height: "45px",
                            }}
                        >
                            {loading ? "Processing..." : `Pay Now $${totalAmount}`}
                        </Button>
                    </form>
                ) : (
                    <Button
                        onClick={handlePayPalPayment}
                        type="default"
                        loading={loading}
                        block
                        style={{
                            background: "#ffc439",
                            color: "#000",
                            border: "none",
                            borderRadius: "50px",
                            fontWeight: 600,
                            fontSize: "16px",
                            height: "45px",
                        }}
                    >
                        {loading ? "Redirecting..." : `Pay with PayPal $${totalAmount}`}
                    </Button>
                )}
            </div>
        </div>
    );
};

export default StripePaymentForm;
